import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import { api } from "@/lib/api";
import { TrendingUp } from "lucide-react";

const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const label = (m) => `${MES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}`;
const num = (v) => Number(v || 0).toLocaleString("pt-BR");
const KIND = { estimado: "estimado pelo SYSLOG", fechamento: "fechamento do mês", parcial: "hoje (mês em andamento)" };

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold text-slate-200">{label(p.month)}</div>
      <div className="font-mono text-lg font-extrabold text-white">{num(p.total)}</div>
      <div className="text-slate-500">{KIND[p.kind]}</div>
    </div>
  );
};

const Dot = ({ cx, cy, payload }) => (
  <circle cx={cx} cy={cy} r={payload.kind === "parcial" ? 6 : 4} strokeWidth={2}
    stroke={payload.kind === "parcial" ? "#F59E0B" : "#38BDF8"} fill={payload.kind === "estimado" ? "#0F141F" : payload.kind === "parcial" ? "#F59E0B" : "#38BDF8"} />
);

export const HistoryChart = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/dados/history").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const first = rows[0]?.total, last = rows[rows.length - 1]?.total;
  const growth = first ? ((100 * (last - first)) / first).toFixed(1) : null;
  return (
    <div data-testid="dados-history" className="rounded-2xl border border-[#1F293D] bg-[#111827] p-5 shadow-xl shadow-black/40 fade-up">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-300"><TrendingUp className="h-4 w-4 text-sky-400" /> Histórico de clientes</h3>
          <p className="mt-1 text-xs text-slate-500">Total de clientes ativos no último minuto de cada mês · mês atual mostra o total de hoje</p>
        </div>
        {growth != null && (
          <div data-testid="dados-history-growth" className="text-right">
            <div className={`font-mono text-xl font-extrabold ${growth >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{growth >= 0 ? "+" : ""}{growth.replace(".", ",")}%</div>
            <div className="text-[11px] text-slate-500">desde {label(rows[0].month)}</div>
          </div>
        )}
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="histFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1F293D" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tickFormatter={label} tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} width={48} domain={[(m) => Math.max(0, Math.floor((m - 150) / 100) * 100), (m) => Math.ceil((m + 150) / 100) * 100]} tickFormatter={num} allowDecimals={false} />
            <Tooltip content={<Tip />} cursor={{ stroke: "#334155" }} />
            <Area type="monotone" dataKey="total" stroke="#38BDF8" strokeWidth={2.5} fill="url(#histFill)" dot={<Dot />} activeDot={{ r: 6 }}>
              <LabelList dataKey="total" position="top" formatter={num} style={{ fill: "#CBD5E1", fontSize: 10, fontFamily: "monospace" }} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border-2 border-sky-400" /> estimado pelo SYSLOG (cadastro × vencimento)</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" /> fechamento gravado</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> mês atual (hoje)</span>
      </div>
    </div>
  );
};
